import * as React from "react";
import { Link as RouterLink, useNavigate } from "react-router";

import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type {
    JourneyInstanceDTO,
    JourneyInstanceMetadata,
    JourneyInstanceMetadataBackhandStyle,
    JourneyInstanceMetadataRatingType,
    JourneyMetadataHand,
    JourneyWithRelations,
    OffsetResult,
    UserWithRelations,
} from "@volley/data";

import { fetchApi, terseTimeFormat } from "../../../util";
import type { Sport } from "../../common/context/sport";
import { useCurrentUser } from "../../hooks/currentUser";
import useJourney from "../../hooks/useJourney";
import { inProgressUrl } from "../journeyNav";

import { ADMINISTER_ASSESSMENT_PROXY_FEATURE } from "./constants";

const STORAGE_KEYS = {
    RATING_TYPE: "assessment_ratingType",
    RATING: "assessment_rating",
    HAND: "assessment_hand",
    BACKHAND_STYLE: "assessment_backhandStyle",
} as const;

const RATING_TYPES: {
    label: string;
    value: JourneyInstanceMetadataRatingType;
}[] = [
    { label: "DUPR®", value: "DUPR" },
    { label: "Club Specific", value: "club_specific" },
];

const HANDS: { label: string; value: JourneyMetadataHand }[] = [
    { label: "Right", value: "right" },
    { label: "Left", value: "left" },
];

const BACKHAND_STYLES: {
    label: string;
    value: JourneyInstanceMetadataBackhandStyle;
}[] = [
    { label: "One-Handed", value: "one_handed" },
    { label: "Two-Handed", value: "two_handed" },
];

interface AssessmentStartDialogProps {
    open: boolean;
    journey: JourneyWithRelations | null;
    inProgressInstance: JourneyInstanceDTO | null;
    onClose: () => void;
    onStart: (metadata: JourneyInstanceMetadata) => Promise<void>;
    sport: Sport;
}

const AssessmentStartDialog: React.FC<AssessmentStartDialogProps> = ({
    open,
    journey,
    inProgressInstance,
    onClose,
    onStart,
    sport,
}) => {
    const { isAdmin, isPro, features } = useCurrentUser();
    const canTransferToOtherUser =
        isAdmin() ||
        isPro() ||
        features.includes(ADMINISTER_ASSESSMENT_PROXY_FEATURE);
    const { logStepEvent, registerJourneyLinkIntent } = useJourney();
    const navigate = useNavigate();

    // Track that the user entered the metadata input step when the dialog
    // opens. `profileWasComplete` is read directly from localStorage (the
    // source of truth for the saved profile) so the value reflects the
    // pre-dialog state regardless of in-flight typing, and the effect
    // doesn't need `hand`/`backhandStyle` as deps.
    React.useEffect(() => {
        if (open && journey) {
            const profileWasComplete =
                !!localStorage.getItem(STORAGE_KEYS.HAND) &&
                !!localStorage.getItem(STORAGE_KEYS.BACKHAND_STYLE);
            logStepEvent("metadata_input", {
                journeyId: journey.id,
                metadata: { profileWasComplete },
            });
        }
    }, [open, journey, logStepEvent]);

    const [ratingType, setRatingType] = React.useState<
        JourneyInstanceMetadataRatingType | ""
    >(() => {
        const stored = localStorage.getItem(STORAGE_KEYS.RATING_TYPE);
        const valid: JourneyInstanceMetadataRatingType[] = [
            "DUPR",
            "club_specific",
        ];
        return valid.includes(stored as JourneyInstanceMetadataRatingType)
            ? (stored as JourneyInstanceMetadataRatingType)
            : "";
    });
    const [rating, setRating] = React.useState(
        () => localStorage.getItem(STORAGE_KEYS.RATING) || "",
    );
    const [hand, setHand] = React.useState<JourneyMetadataHand | "">(
        () =>
            (localStorage.getItem(STORAGE_KEYS.HAND) as JourneyMetadataHand) ||
            "",
    );
    const [backhandStyle, setBackhandStyle] = React.useState<
        JourneyInstanceMetadataBackhandStyle | ""
    >(
        () =>
            (localStorage.getItem(
                STORAGE_KEYS.BACKHAND_STYLE,
            ) as JourneyInstanceMetadataBackhandStyle) || "",
    );

    // Subject user state (admin only)
    const [subjectUser, setSubjectUser] = React.useState("");
    const [isValidating, setIsValidating] = React.useState(false);
    const [validationError, setValidationError] = React.useState("");
    const [validUser, setValidUser] = React.useState<UserWithRelations | null>(
        null,
    );
    const abortControllerRef = React.useRef<AbortController | null>(null);

    const resumeUrl = React.useMemo(() => {
        if (!inProgressInstance) return "";
        if (inProgressInstance.workoutsPlays.length === 1) {
            const aw = inProgressInstance.workoutsPlays[0].appWorkout;
            return `/content/apps/workouts/plugin/play/${aw.appId}/${aw.id}`;
        }
        return inProgressUrl({
            journeyType: "assessment",
            journeyInstanceId: inProgressInstance.id,
        });
    }, [inProgressInstance]);

    const handleResumeClick = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Single-workout assessments navigate straight to play; intent
            // must be registered first so the play links back to this slot.
            // Multi-workout assessments resume on the in-progress page, where
            // intent is registered when the user picks the next workout.
            if (inProgressInstance?.workoutsPlays.length !== 1) {
                return;
            }
            e.preventDefault();
            const aw = inProgressInstance.workoutsPlays[0].appWorkout;
            await registerJourneyLinkIntent(inProgressInstance.ulid, aw.id);
            navigate(resumeUrl);
        },
        [inProgressInstance, registerJourneyLinkIntent, navigate, resumeUrl],
    );

    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [isStarting, setIsStarting] = React.useState(false);
    const [startError, setStartError] = React.useState("");

    React.useEffect(() => {
        if (ratingType)
            localStorage.setItem(STORAGE_KEYS.RATING_TYPE, ratingType);
        if (rating) localStorage.setItem(STORAGE_KEYS.RATING, rating);
        if (hand) localStorage.setItem(STORAGE_KEYS.HAND, hand);
        if (backhandStyle)
            localStorage.setItem(STORAGE_KEYS.BACKHAND_STYLE, backhandStyle);
    }, [ratingType, rating, hand, backhandStyle]);

    // Validate subject user function
    const validateUser = async (input: string) => {
        const trimmedInput = input.trim();

        // Cancel any previous validation request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (!trimmedInput) {
            setValidationError("");
            setValidUser(null);
            return;
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmail = emailRegex.test(trimmedInput);

        if (!isEmail && trimmedInput.includes("@")) {
            setValidationError("Invalid email format");
            setValidUser(null);
            return;
        }

        setIsValidating(true);
        setValidationError("");

        // Create new AbortController for this request
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const url = `/api/users/search?q=${encodeURIComponent(trimmedInput)}&limit=1&offset=0`;
            const result = await fetchApi<OffsetResult<UserWithRelations>>(
                url,
                "GET",
                undefined,
                { signal: controller.signal },
            );

            if (result.count > 0 && result.result.length > 0) {
                const user = result.result[0];
                // Check for exact match
                if (
                    user.email?.toLowerCase() === trimmedInput.toLowerCase() ||
                    user.username?.toLowerCase() === trimmedInput.toLowerCase()
                ) {
                    setValidUser(user);
                    setValidationError("");
                } else {
                    setValidUser(null);
                    setValidationError(
                        "User not found with this exact email/username",
                    );
                }
            } else {
                setValidUser(null);
                setValidationError("User not found");
            }
        } catch (error) {
            // Don't show error if request was aborted
            if (error instanceof Error && error.name === "AbortError") {
                return;
            }
            setValidationError("Error validating user");
            setValidUser(null);
        } finally {
            // Only clear loading state if this request wasn't aborted
            if (abortControllerRef.current === controller) {
                setIsValidating(false);
                abortControllerRef.current = null;
            }
        }
    };

    // Debounced validation effect for subject user
    React.useEffect(() => {
        let timer: NodeJS.Timeout | undefined;

        if (subjectUser) {
            timer = setTimeout(() => {
                validateUser(subjectUser);
            }, 500);
        } else {
            setValidationError("");
            setValidUser(null);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            // Cancel any in-flight request on cleanup
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [subjectUser]);

    const handleStartClick = () => {
        if (inProgressInstance) {
            setShowConfirmation(true);
        } else {
            handleConfirmedStart();
        }
    };

    const handleConfirmedStart = async () => {
        setIsStarting(true);
        setStartError("");
        const metadata: JourneyInstanceMetadata = {
            subjectUser: subjectUser || undefined,
            hand: hand || undefined,
            side:
                hand === "right" ? "even" : hand === "left" ? "odd" : undefined,
            backhandStyle: backhandStyle || undefined,
            userDescribedRating:
                ratingType && rating ? { type: ratingType, rating } : null,
        };

        try {
            await onStart(metadata);
            setShowConfirmation(false);
        } catch {
            setStartError("Something went wrong. Please try again.");
        } finally {
            setIsStarting(false);
        }
    };

    const handleClose = () => {
        setShowConfirmation(false);
        onClose();
    };

    if (!journey) return null;

    return (
        <>
            <Dialog
                open={open && !showConfirmation}
                onClose={handleClose}
                fullScreen
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    Start {journey.title}
                    <IconButton onClick={handleClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {canTransferToOtherUser && (
                            <>
                                <Typography variant="subtitle2">
                                    Transfer to another user (optional)
                                </Typography>
                                <TextField
                                    type="text"
                                    label="Username or email"
                                    value={subjectUser}
                                    onChange={(e) =>
                                        setSubjectUser(e.target.value)
                                    }
                                    helperText={
                                        isValidating
                                            ? "Validating..."
                                            : validUser
                                              ? `Volley user: ${validUser.firstName} ${validUser.lastName}`
                                              : validationError
                                                ? `Not a Volley user`
                                                : undefined
                                    }
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            sx: {
                                                "&.Mui-focused": {
                                                    color: "inherit",
                                                },
                                            },
                                        },
                                        formHelperText: {
                                            sx: {
                                                color: validUser
                                                    ? "success.main"
                                                    : validationError
                                                      ? "info.light"
                                                      : undefined,
                                            },
                                        },
                                    }}
                                />
                            </>
                        )}

                        <Typography variant="subtitle2">
                            Required for Setup
                        </Typography>

                        <FormControl fullWidth required>
                            <InputLabel
                                sx={{
                                    "&.Mui-focused": {
                                        color: "inherit",
                                    },
                                }}
                            >
                                Dominant Hand
                            </InputLabel>
                            <Select
                                label="Dominant Hand"
                                value={hand}
                                onChange={(e) =>
                                    setHand(
                                        e.target.value as JourneyMetadataHand,
                                    )
                                }
                            >
                                {HANDS.map((h) => (
                                    <MenuItem key={h.value} value={h.value}>
                                        {h.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth required>
                            <InputLabel
                                sx={{
                                    "&.Mui-focused": {
                                        color: "inherit",
                                    },
                                }}
                            >
                                Backhand Style
                            </InputLabel>
                            <Select
                                label="Backhand Style"
                                value={backhandStyle}
                                onChange={(e) =>
                                    setBackhandStyle(
                                        e.target
                                            .value as JourneyInstanceMetadataBackhandStyle,
                                    )
                                }
                            >
                                {BACKHAND_STYLES.map((b) => (
                                    <MenuItem key={b.value} value={b.value}>
                                        {b.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {sport === "PICKLEBALL" && (
                            <>
                                <Typography variant="subtitle2">
                                    Your Current Rating (optional)
                                </Typography>
                                <Stack direction="row" spacing={2}>
                                    <FormControl sx={{ minWidth: 150 }}>
                                        <InputLabel
                                            sx={{
                                                "&.Mui-focused": {
                                                    color: "inherit",
                                                },
                                            }}
                                        >
                                            Type
                                        </InputLabel>
                                        <Select
                                            label="Type"
                                            value={ratingType}
                                            onChange={(e) =>
                                                setRatingType(
                                                    e.target
                                                        .value as JourneyInstanceMetadataRatingType,
                                                )
                                            }
                                        >
                                            {RATING_TYPES.map((t) => (
                                                <MenuItem
                                                    key={t.value}
                                                    value={t.value}
                                                >
                                                    {t.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label="Rating"
                                        value={rating}
                                        onChange={(e) =>
                                            setRating(e.target.value)
                                        }
                                        inputMode="decimal"
                                        sx={{
                                            width: 120,
                                            "&.Mui-focused": {
                                                color: "inherit",
                                            },
                                        }}
                                    />
                                </Stack>
                            </>
                        )}

                        <Divider />

                        <Typography variant="subtitle2">
                            Workouts in this assessment:
                        </Typography>
                        <List dense>
                            {journey.journeyAppWorkouts.map((w, index) => (
                                <ListItem key={w.id}>
                                    <ListItemAvatar>
                                        <Avatar
                                            sx={{
                                                bgcolor: "secondary.main",
                                                width: 28,
                                                height: 28,
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            {index + 1}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={w.appWorkout.name} />
                                </ListItem>
                            ))}
                        </List>

                        {inProgressInstance && (
                            <Alert
                                severity="warning"
                                icon={<WarningAmberIcon />}
                                sx={{ mt: 2 }}
                            >
                                <Typography variant="body2" gutterBottom>
                                    An unfinished assessment from{" "}
                                    {terseTimeFormat(
                                        new Date(inProgressInstance.startTime),
                                    )}{" "}
                                    exists. Starting a new assessment above will
                                    permanently delete your pending progress.
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    component={RouterLink}
                                    to={resumeUrl}
                                    onClick={handleResumeClick}
                                    sx={{ mt: 1 }}
                                >
                                    Resume Existing
                                </Button>
                            </Alert>
                        )}

                        {startError && (
                            <Alert severity="error">{startError}</Alert>
                        )}

                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleStartClick}
                            disabled={!hand || !backhandStyle || isStarting}
                            fullWidth
                            size="large"
                            sx={{ color: "primary.main", mt: 2 }}
                        >
                            Let’s Go!
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showConfirmation}
                onClose={() => setShowConfirmation(false)}
            >
                <DialogContent>
                    <Typography>
                        Are you sure you want to start a new assessment? This
                        will permanently delete your in-progress assessment from{" "}
                        {inProgressInstance &&
                            terseTimeFormat(
                                new Date(inProgressInstance.startTime),
                            )}
                        .
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowConfirmation(false)}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmedStart}
                        color="error"
                        disabled={isStarting}
                    >
                        Delete & Start New
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AssessmentStartDialog;
